import React, { useMemo, useState } from 'react';

/**
 * GitHub-style publishing activity calendar.
 * Shows 12 weeks back + 8 weeks forward from today.
 * Green = pages created, Blue = scheduled future pages, Red border = today.
 */
const PublishingCalendar = ({ pageActivity = {}, currentStreak = 0, longestStreak = 0 }) => {
  const [tooltip, setTooltip] = useState(null); // { text, x, y }

  const { weeks, monthLabels } = useMemo(() => {
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);

    // Start 12 weeks ago, aligned to Monday
    const start = new Date(now);
    start.setDate(start.getDate() - 84); // 12 weeks back
    // Align to Monday (getDay: 0=Sun,1=Mon..6=Sat)
    const dayOfWeek = start.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    start.setDate(start.getDate() + mondayOffset);
    start.setHours(0, 0, 0, 0);

    // End 8 weeks forward from today
    const end = new Date(now);
    end.setDate(end.getDate() + 56);
    // Extend to Sunday
    const endDay = end.getDay();
    if (endDay !== 0) {
      end.setDate(end.getDate() + (7 - endDay));
    }

    const weeksArr = [];
    const months = [];
    let lastMonth = -1;
    const cursor = new Date(start);

    while (cursor <= end) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        const dateStr = cursor.toISOString().slice(0, 10);
        const month = cursor.getMonth();
        const weekIdx = weeksArr.length;
        if (month !== lastMonth) {
          months.push({ month, weekIdx, label: cursor.toLocaleDateString('en-US', { month: 'short' }) });
          lastMonth = month;
        }
        const activity = pageActivity[dateStr] || { created: 0, scheduled: 0 };
        week.push({
          date: dateStr,
          created: activity.created || 0,
          scheduled: activity.scheduled || 0,
          isToday: dateStr === todayStr,
          isFuture: dateStr > todayStr,
          isPast: dateStr < todayStr,
        });
        cursor.setDate(cursor.getDate() + 1);
      }
      weeksArr.push(week);
    }

    return { weeks: weeksArr, monthLabels: months, today: todayStr };
  }, [pageActivity]);

  const getCellColor = (day) => {
    if (day.isFuture) {
      if (day.scheduled > 0) return '#79b8ff'; // blue for scheduled
      return '#f6f8fa'; // very light gray for future empty
    }
    const count = day.created;
    if (count === 0) return '#ebedf0';
    if (count === 1) return '#9be9a8';
    if (count === 2) return '#40c463';
    return '#216e39'; // 3+
  };

  const cellSize = 14;
  const cellGap = 3;
  const labelWidth = 32;
  const headerHeight = 18;
  const dayLabels = ['Mon', '', 'Wed', '', 'Fri', '', ''];
  const gridWidth = labelWidth + weeks.length * (cellSize + cellGap);
  const gridHeight = headerHeight + 7 * (cellSize + cellGap);

  const handleMouseEnter = (e, day) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const d = new Date(day.date + 'T12:00:00');
    const formatted = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    let text = formatted;
    if (day.isFuture) {
      text += day.scheduled > 0 ? ` — ${day.scheduled} scheduled` : ' — No pages scheduled';
    } else {
      text += day.created > 0 ? ` — ${day.created} page${day.created > 1 ? 's' : ''} created` : ' — No pages created';
    }
    setTooltip({ text, x: rect.left + rect.width / 2, y: rect.top - 8 });
  };

  const handleMouseLeave = () => setTooltip(null);

  const streakEmoji = currentStreak >= 14 ? '🔥🔥' : currentStreak >= 7 ? '🔥' : currentStreak >= 3 ? '⚡' : '📝';

  return (
    <div className="card">
      <h3 className="card-title">Publishing activity</h3>
      {/* Streak Banner */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
          <span style={{ fontSize: '24px' }}>{streakEmoji}</span>
          <span style={{
            fontSize: '28px',
            fontWeight: '800',
            color: currentStreak > 0 ? '#216e39' : '#888',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {currentStreak}
          </span>
          <span style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#495057',
          }}>
            day streak
          </span>
        </div>
        <div style={{
          fontSize: '13px',
          color: '#888',
          display: 'flex',
          gap: '16px',
        }}>
          <span>Longest: <strong style={{ color: '#495057' }}>{longestStreak}</strong> days</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div style={{ overflowX: 'auto', paddingBottom: '4px' }}>
        <svg width={gridWidth} height={gridHeight + 10} style={{ display: 'block' }}>
          {/* Month labels */}
          {monthLabels.map((m, i) => (
            <text
              key={i}
              x={labelWidth + m.weekIdx * (cellSize + cellGap)}
              y={12}
              fontSize="10"
              fill="#888"
              fontFamily="system-ui, -apple-system, sans-serif"
            >
              {m.label}
            </text>
          ))}

          {/* Day labels */}
          {dayLabels.map((label, i) => (
            label ? (
              <text
                key={i}
                x={0}
                y={headerHeight + i * (cellSize + cellGap) + cellSize - 2}
                fontSize="10"
                fill="#888"
                fontFamily="system-ui, -apple-system, sans-serif"
              >
                {label}
              </text>
            ) : null
          ))}

          {/* Cells */}
          {weeks.map((week, wi) =>
            week.map((day, di) => (
              <rect
                key={day.date}
                x={labelWidth + wi * (cellSize + cellGap)}
                y={headerHeight + di * (cellSize + cellGap)}
                width={cellSize}
                height={cellSize}
                rx={2}
                ry={2}
                fill={getCellColor(day)}
                stroke={day.isToday ? '#e53e3e' : 'transparent'}
                strokeWidth={day.isToday ? 2 : 0}
                style={{ cursor: 'pointer', outline: 'none' }}
                onMouseEnter={(e) => handleMouseEnter(e, day)}
                onMouseLeave={handleMouseLeave}
              />
            ))
          )}

          {/* "Today" marker line — vertical for visual anchor */}
          {(() => {
            // Find which week column contains today
            for (let wi = 0; wi < weeks.length; wi++) {
              if (weeks[wi].some(d => d.isToday)) {
                const x = labelWidth + wi * (cellSize + cellGap) + cellSize / 2;
                return (
                  <line
                    x1={x} y1={headerHeight - 3}
                    x2={x} y2={headerHeight + 7 * (cellSize + cellGap) + 2}
                    stroke="#e53e3e"
                    strokeWidth={0.5}
                    strokeDasharray="2,2"
                    opacity={0.4}
                  />
                );
              }
            }
            return null;
          })()}
        </svg>
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginTop: '8px',
        fontSize: '11px',
        color: '#888',
        flexWrap: 'wrap',
      }}>
        <span>Less</span>
        {['#ebedf0', '#9be9a8', '#40c463', '#216e39'].map((color, i) => (
          <span
            key={i}
            style={{
              display: 'inline-block',
              width: '12px',
              height: '12px',
              backgroundColor: color,
              borderRadius: '2px',
            }}
          />
        ))}
        <span>More</span>
        <span style={{ marginLeft: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            display: 'inline-block',
            width: '12px',
            height: '12px',
            backgroundColor: '#79b8ff',
            borderRadius: '2px',
          }} />
          Scheduled
        </span>
        <span style={{ marginLeft: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            display: 'inline-block',
            width: '12px',
            height: '12px',
            backgroundColor: '#fff',
            borderRadius: '2px',
            border: '2px solid #e53e3e',
            boxSizing: 'border-box',
          }} />
          Today
        </span>
      </div>

      {/* Tooltip (portal-free, fixed position) */}
      {tooltip && (
        <div style={{
          position: 'fixed',
          left: tooltip.x,
          top: tooltip.y,
          transform: 'translate(-50%, -100%)',
          backgroundColor: '#24292e',
          color: '#fff',
          padding: '6px 10px',
          borderRadius: '4px',
          fontSize: '12px',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          zIndex: 10000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>
          {tooltip.text}
        </div>
      )}
    </div>
  );
};

export default PublishingCalendar;
